import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { LocationMenuDialogs } from "../types/dialogs"

import { Button } from "@/components/common/Button"
import useHandleCloseWithDataSave from "@/screens/Companies/edit/components/locations/hooks/useHandleCloseWithDataSave"
import { LocationAddressDetailsForm } from "@/screens/Companies/edit/components/locations/LocationAddressDetailsForm"
import { useGetCountriesAndRegions } from "@/screens/Companies/hooks/useGetCountriesAndRegions"
import {
  AddressUpdateSchema,
  type EditAddressDialogProps,
  type EditAddressQueryProps,
} from "@/screens/Companies/types/dialogProps"

export const EditAddressDialog: FC<EditAddressDialogProps> = ({ data, onClose, onSubmit, activeEditLocation }) => {
  const initialFormValues = {
    name: activeEditLocation?.name,
    description: activeEditLocation?.description,
    address1: activeEditLocation?.address1,
    address2: activeEditLocation?.address2 || "",
    city: activeEditLocation?.city,
    postalCode: activeEditLocation?.postalCode,
    countryIsoCode: activeEditLocation?.country?.isoCode,
    regionIsoCode: activeEditLocation?.regionIsoCode,
    as400: {
      name: activeEditLocation?.as400Name,
      address1: activeEditLocation?.as400Address1,
      address2: activeEditLocation?.as400Address2,
      city: activeEditLocation?.as400City,
      regionIsoCode: activeEditLocation?.as400State,
      postalCode: activeEditLocation?.as400PostalCode,
    },
  } as EditAddressQueryProps

  const methods = useForm<EditAddressQueryProps>({
    values: { ...initialFormValues, ...data },
    resolver: zodResolver(AddressUpdateSchema),
    mode: "onChange",
  })

  const { trigger } = methods

  const handleClose = useHandleCloseWithDataSave(methods.getValues, onClose)

  const { countries, regions, setSelectedCountry } = useGetCountriesAndRegions(activeEditLocation?.country?.isoCode)
  const countryIsoCode = methods.watch("countryIsoCode")

  // TODO: exlore how to set the country on the form
  useEffect(() => {
    if (countryIsoCode) {
      setSelectedCountry(countryIsoCode)
    }
  }, [countryIsoCode, setSelectedCountry])

  const handleSave = useCallback(
    async (value: EditAddressQueryProps) => {
      const isValid = await trigger()
      if (isValid) {
        onSubmit(value)
        onClose({ data: {}, reason: "success" })
      }
    },
    [onSubmit, onClose, trigger]
  )

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      fullWidth
      PaperProps={{ "data-testid": "add-address-dialog", sx: { maxWidth: "76rem" } }}
    >
      <DialogTitle className='pl-10'>
        <span className='font-normal text-primary'>Edit Location</span>
      </DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationAddressDetailsForm
            countryList={countries}
            regionList={regions}
            locationType={activeEditLocation?.externalLocationType ?? undefined}
            modalType={LocationMenuDialogs.EDIT_ADDRESS}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button variant='primary' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={methods.handleSubmit(handleSave)} disabled={!methods.formState.isValid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
